import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, filter, take, timeout, catchError, of } from 'rxjs';
import { User } from '@angular/fire/auth';

import { PlatformService } from '@app/core/platform/platform.service';
import { EngagementService } from '@app/core/services/engagement.service';
import { isAdminUser } from '../admin/admin.config';
import { AuthService } from '../services/auth.service';

/**
 * Route guard: only the engagement's owner (buyerId) — or an admin — may open
 * /engagements/:id and its children. Everyone else is redirected home.
 *
 * SSR note: on the server there's no authenticated user, so we allow the
 * navigation to proceed and let the browser re-run the guard once auth
 * resolves. The real check runs client-side.
 *
 * This is a UX-layer protection (stops snooping via the app). It is NOT a
 * substitute for Firestore security rules for direct database access.
 */
export const engagementOwnerGuard: CanActivateFn = async (route) => {
  const auth = inject(AuthService);
  const platform = inject(PlatformService);
  const router = inject(Router);
  const engagements = inject(EngagementService);

  // Don't block during server render — re-checked in the browser.
  if (platform.isServer) return true;

  const id = route.paramMap.get('id') ?? '';
  if (!id) {
    router.navigateByUrl('/');
    return false;
  }

  // Resolve the current user (wait for auth to settle).
  const user = await firstValueFrom(
    auth.user$.pipe(
      filter((u): u is User | null => u !== undefined),
      take(1),
      timeout(4000),
      catchError(() => of(null)),
    ),
  );

  if (!user) {
    router.navigateByUrl('/login');
    return false;
  }

  // Admins can open anything.
  if (isAdminUser(user.email, user.uid)) return true;

  // Otherwise the engagement must belong to this user.
  try {
    const engagement = await engagements.getById(id);
    if (engagement && engagement.buyerId === user.uid) {
      return true;
    }
  } catch {
    // fall through to redirect
  }

  router.navigateByUrl('/');
  return false;
};